/* istanbul ignore file */
import useFieldHelper from '../fieldHelper';
import { FieldProps } from '../types';
import React from 'react';
import ProgressIndicator from './ProgressIndicator';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { UiThemeOverrider } from 'framework/uiTheme';
import { EMPTY_VALUE_INDICATOR } from 'framework/utils';

const STATUS_COLORS = {
  IN_PROGRESS: 'info.main',
  IN_PROGRESS_WITH_DELAY: 'error.main',
  COMPLETED: 'success.main',
  COMPLETED_WITH_DELAY: 'warning.main',
} as const;

const LARGE_INDICATOR_STATUSES = new Set(['COMPLETED', 'COMPLETED_WITH_DELAY', 'IN_PROGRESS_WITH_DELAY']);

const ProgressFieldLayout = ({ value, label, className = '' }: FieldProps) => {
  return (
    <Typography variant="body1" component="div" className={className}>
      <Typography variant="subtitle1">{label as string}:</Typography> {value || EMPTY_VALUE_INDICATOR}
    </Typography>
  );
};

const ProgressField = <T,>({
  source,
  steps,
  children,
  additionalData,
  getTooltipTitle,
  getIcon,
}: {
  source: string;
  steps: string[];
  children: any;
  additionalData?: any;
  getTooltipTitle?: (data: any) => React.ReactNode;
  getIcon: (status: T) => React.ReactNode;
}) => {
  const { getValue } = useFieldHelper({ source });
  const progressData = (getValue() || {}) as Dictionary<any>;

  const mappedProgressData = Array.isArray(progressData)
    ? progressData.reduce((result, value) => {
        return { ...result, [value.stepName]: value };
      }, {})
    : {};

  return (
    <Stack
      className="project-dashboard-timeline"
      direction="row"
      alignItems="flex-start"
      gap="4px"
      sx={{ width: '100%' }}
    >
      {steps.map((field: string, index: number) => {
        const { status = '' } = mappedProgressData[field] || {};
        const normalizedStatus = status.toUpperCase();
        const statusColor = STATUS_COLORS[normalizedStatus as keyof typeof STATUS_COLORS] || 'text.secondary';
        const hasLargeIndicator = LARGE_INDICATOR_STATUSES.has(normalizedStatus);

        if (!mappedProgressData[field]) {
          return null;
        }

        const Item = React.forwardRef((props: Dictionary<any>, ref: any) => (
          <Box
            {...props}
            ref={ref}
            className="project-dashboard-timeline__item"
            sx={{
              display: 'flex',
              flex: '1 1 0',
              flexDirection: 'column',
              minWidth: '120px',
              '& h3': {
                marginBottom: 'auto',
                color: statusColor,
                fontSize: '14px',
                fontWeight: 600,
                lineHeight: 1.25,
                overflowWrap: 'normal',
                whiteSpace: 'pre-wrap',
              },
            }}
          >
            <Box
              className="project-dashboard-timeline__item-text"
              sx={{
                display: 'flex',
                flex: '1 1 auto',
                width: '100%',
                flexDirection: 'column',
                minWidth: '120px',
                paddingRight: '5px',
                marginBottom: '12px',
                '& p': {
                  margin: 0,
                  color: statusColor,
                  whiteSpace: 'nowrap',
                  '&:first-of-type': { marginTop: '9px' },
                },
              }}
            >
              {React.cloneElement(children, { ...(mappedProgressData[field] || {}), field, index })}
            </Box>

            <ProgressIndicator
              status={normalizedStatus}
              color={statusColor}
              icon={getIcon(status)}
              isLarge={hasLargeIndicator}
            />

            {additionalData ? (
              <UiThemeOverrider overriddenLayouts={{ BaseFieldLayout: ProgressFieldLayout }}>
                <Box
                  className="project-dashboard-timeline__item-additional-text"
                  sx={{
                    '&:not(:empty)': {
                      backgroundColor: 'background.default',
                      marginTop: '10px',
                      padding: '7px 5px',
                      fontSize: '13px',
                    },
                  }}
                >
                  {React.cloneElement(additionalData, { ...(mappedProgressData[field] || {}), field, index })}
                </Box>
              </UiThemeOverrider>
            ) : null}
          </Box>
        ));

        const tooltipTitle = getTooltipTitle
          ? getTooltipTitle({ ...(mappedProgressData[field] || {}), field, index })
          : null;

        return tooltipTitle ? (
          <Tooltip title={tooltipTitle} arrow>
            <Item />
          </Tooltip>
        ) : (
          <Item />
        );
      })}
    </Stack>
  );
};

export default ProgressField;
